import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './Contact.css';

// Import local assets
import studioImg from '../assets/spices.jpg';

// ── Email notification via Formsubmit.co ──────────────────────
// No API key needed — emails go directly to digisahyadriit@gmail.com
// IMPORTANT: The FIRST time you submit, Formsubmit will send a
// confirmation email to digisahyadriit@gmail.com — click the
// "Confirm" link in that email. After that, all emails work automatically.
const NOTIFY_EMAIL = 'digisahyadriit@gmail.com';

const Contact = () => {
  // ── Form state ──
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: 'General Inquiry',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [statusMessage, setStatusMessage] = useState('');

  // ── Handlers ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) newErrors.message = 'Please enter your message';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('sending');

    try {
      // 1️⃣ Save to Firebase Firestore
      await addDoc(collection(db, 'contactInquiries'), {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        subject: formData.subject,
        message: formData.message.trim(),
        createdAt: serverTimestamp(),
      });

      // 2️⃣ Send email notification via Formsubmit.co (no API key needed)
      try {
        await fetch(`https://formsubmit.co/ajax/${NOTIFY_EMAIL}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            _subject: `🌶️ New Inquiry: ${formData.subject}`,
            'Full Name': formData.fullName.trim(),
            'Email Address': formData.email.trim(),
            'Subject': formData.subject,
            'Message': formData.message.trim(),
            _template: 'table',
          }),
        });
      } catch (emailErr) {
        console.warn('Email notification failed (non-blocking):', emailErr);
      }

      // ✅ Success
      setStatus('success');
      setStatusMessage('Your inquiry has been sent successfully! We\'ll get back to you soon.');
      setFormData({ fullName: '', email: '', subject: 'General Inquiry', message: '' });

      // Auto-dismiss after 6 seconds
      setTimeout(() => setStatus('idle'), 6000);
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
      setStatusMessage('Something went wrong. Please try again or contact us directly.');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  return (
    <div className="contact-page">
      {/* ── TOAST NOTIFICATION ── */}
      <AnimatePresence>
        {(status === 'success' || status === 'error') && (
          <motion.div
            className={`contact-toast ${status}`}
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          >
            <span className="toast-icon">{status === 'success' ? '✓' : '!'}</span>
            <span className="toast-text">{statusMessage}</span>
            <button className="toast-close" onClick={() => setStatus('idle')}>✕</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DIRECT INQUIRY SECTION */}
      <section className="contact-main-boutique">
        <div className="container">
          <div className="contact-grid-premium">
            {/* NARRATIVE & INFO */}
            <motion.div 
              className="contact-narrative"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="premium-label-box">
                <span className="line" />
                GET IN TOUCH
              </div>
              <h1 className="contact-huge-title">
                Let's Start a <br />
                <span className="serif-italic">Conversation</span>
              </h1>
              
              <div className="contact-channels">
                <div className="channel-item-boutique">
                  <div className="channel-icon-wrap">📍</div>
                  <div>
                    <h4>Visit our studio</h4>
                    <p>123 Spice Garden, Near Shaniwar Wada,<br />Pune, MH 411002</p>
                  </div>
                </div>

                <div className="channel-item-boutique">
                  <div className="channel-icon-wrap">📞</div>
                  <div>
                    <h4>Direct line</h4>
                    <p>+91 98765 43210</p>
                  </div>
                </div>

                <div className="channel-item-boutique">
                  <div className="channel-icon-wrap">✉️</div>
                  <div>
                    <h4>Email support</h4>
                    <p>hello@raginimasale.com</p>
                  </div>
                </div>
              </div>

              <div className="social-sidebar-wrap">
                <h4>FOLLOW OUR JOURNEY</h4>
                <div className="social-pills-row">
                  {['Instagram', 'Facebook', 'LinkedIn'].map((s, i) => (
                    <motion.a 
                      key={s} 
                      href="#" 
                      className="social-pill-premium"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + (i * 0.1) }}
                    >
                      {s}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* PREMIUM FORM CARD */}
            <motion.div 
              className="contact-form-glass-card"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <form className="boutique-form" onSubmit={handleSubmit} noValidate>
                <div className="premium-form-row">
                  <div className={`premium-input-group ${errors.fullName ? 'has-error' : ''}`}>
                    <label htmlFor="contact-fullname">Full Name</label>
                    <input
                      id="contact-fullname"
                      type="text"
                      name="fullName"
                      placeholder="Your name"
                      value={formData.fullName}
                      onChange={handleChange}
                    />
                    {errors.fullName && <span className="field-error">{errors.fullName}</span>}
                  </div>
                  <div className={`premium-input-group ${errors.email ? 'has-error' : ''}`}>
                    <label htmlFor="contact-email">Email Address</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      placeholder="email@address.com"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                </div>

                <div className="premium-input-group" style={{ marginBottom: '2rem' }}>
                  <label htmlFor="contact-subject">Subject of inquiry</label>
                  <select
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  >
                    <option>General Inquiry</option>
                    <option>Wholesale &amp; Distribution</option>
                    <option>Recipe Collaboration</option>
                    <option>Order Support</option>
                  </select>
                </div>

                <div className={`premium-input-group ${errors.message ? 'has-error' : ''}`}>
                  <label htmlFor="contact-message">Your Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows="6"
                    placeholder="How can we help you today?"
                    value={formData.message}
                    onChange={handleChange}
                  />
                  {errors.message && <span className="field-error">{errors.message}</span>}
                </div>

                <motion.button 
                  type="submit" 
                  className={`btn-submit-premium ${status === 'sending' ? 'is-sending' : ''}`}
                  whileHover={status !== 'sending' ? { scale: 1.02 } : {}}
                  whileTap={status !== 'sending' ? { scale: 0.98 } : {}}
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? (
                    <span className="btn-loading-content">
                      <span className="spinner" />
                      Sending…
                    </span>
                  ) : (
                    'Send Inquiry'
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LOCATION SHOWCASE */}
      <section className="location-showcase">
        <div className="container">
          <motion.div 
            className="map-card-premium"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <div className="map-info-floating">
              <span className="premium-accent-tag">Visit our space</span>
              <h3>The Spice <span className="serif-italic">Studio</span></h3>
              <p>Experience the aroma of our traditional stone-grinding process. Our studio is located in the heart of Pune's heritage district.</p>
              <button className="btn-primary">Open in Google Maps</button>
            </div>
            <img src={studioImg} alt="Culinary Studio" />
          </motion.div>
        </div>
      </section>

      {/* BOUTIQUE CTA BANNER (Moved from other pages) */}
      <section className="boutique-cta-banner">
        <div className="container">
          <motion.div 
            className="cta-banner-inner"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <h3>Elevate Your <span className="serif-italic">Culinary Art</span></h3>
            <p>Ready to bring authentic stone-ground spices to your kitchen?</p>
            <Link to="/contact" className="cta-link-premium">Wholesale &amp; Retail Inquiry</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
